import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../../api/client';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  LinearProgress,
  Avatar,
  Stack,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  People as PeopleIcon,
  Computer as ComputerIcon,
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayIcon,
  Coffee as CoffeeIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import {
  fetchQueuePatterns,
  fetchQueuePatternAssignment,
  QueuePatternAssignment,
  QueueServicePattern,
} from '../../api/queuePatterns';
import QueuePatternAssignmentDialog from '../../components/QueuePatterns/QueuePatternAssignmentDialog';

export const OperatorsPage: React.FC = () => {
  const [selectedOperator, setSelectedOperator] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'session' | 'view'>('session');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sessionOperatorId, setSessionOperatorId] = useState<string>('');

  const [patternAssignments, setPatternAssignments] = useState<Record<string, QueuePatternAssignment | null>>({});
  const patternAssignmentLoadedRef = useRef<Set<string>>(new Set());
  const [patternDialogState, setPatternDialogState] = useState<{ open: boolean; operator: any | null }>({
    open: false,
    operator: null,
  });

  const queryClient = useQueryClient();

  // Загрузка данных
  const { data: operatorsData, isLoading, error, refetch } = useQuery(['operators'], async () => {
    const res = await apiClient.get('/api/operators');
    return res.data;
  }, { refetchInterval: 15000 });

  const { data: workstationsData } = useQuery(['operatorWorkstations'], async () => {
    const res = await apiClient.get('/api/operators/workstations');
    return res.data;
  });

  const { data: queuePatternsData } = useQuery(
    ['queue-service-patterns', 'with-inactive'],
    () => fetchQueuePatterns(true),
    { staleTime: 60_000 },
  );
  const queuePatterns: QueueServicePattern[] = queuePatternsData ?? [];

  // Мутации
  const updateStatusMutation = useMutation(
    ({ operatorId, status }: { operatorId: string; status: string }) => 
      apiClient.put(`/api/operators/${operatorId}/status`, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['operators']);
        setSnackbar({ open: true, message: 'Статус обновлен', severity: 'success' });
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Ошибка обновления статуса', severity: 'error' });
      },
    }
  );

  const startSessionMutation = useMutation(
    ({ operatorId, workstationId, queueIds }: { operatorId: string; workstationId?: string | null; queueIds?: string[] }) => 
      apiClient.post(`/api/operators/${operatorId}/sessions/start`, { workstationId, queueIds }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['operators']);
        setSnackbar({ open: true, message: 'Сессия начата', severity: 'success' });
        setDialogOpen(false);
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Не удалось начать сессию', severity: 'error' });
      },
    }
  );

  const endSessionMutation = useMutation(
    ({ operatorId }: { operatorId: string }) => apiClient.post(`/api/operators/${operatorId}/sessions/end`, {}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['operators']);
        setSnackbar({ open: true, message: 'Сессия завершена', severity: 'success' });
      },
      onError: () => {
        setSnackbar({ open: true, message: 'Не удалось завершить сессию', severity: 'error' });
      },
    }
  );

  // Обработчики событий
  const handleOpenDialog = (type: 'session' | 'view', operator?: any) => {
    setDialogType(type);
    setSelectedOperator(operator || null);
    setSessionOperatorId(operator?.id || '');
    setDialogOpen(true);
  };

  const handleOpenPatternDialog = (operator: any) => {
    setPatternDialogState({ open: true, operator });
    if (operator?.id) {
      patternAssignmentLoadedRef.current.delete(operator.id);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedOperator(null);
  };

  const handleStatusChange = (operatorId: string, newStatus: string) => {
    updateStatusMutation.mutate({ operatorId, status: newStatus });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'busy': return 'warning';
      case 'break': return 'info';
      case 'offline': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return 'Доступен';
      case 'busy': return 'Занят';
      case 'break': return 'Перерыв';
      case 'offline': return 'Офлайн';
      default: return status;
    }
  };

  const handleManualRefresh = async () => {
    try {
      const result = await refetch();
      await Promise.all([
        queryClient.invalidateQueries(['operatorWorkstations']),
        queryClient.invalidateQueries(['queue-service-patterns', 'with-inactive']),
      ]);

      if (result.error) {
        throw result.error;
      }

      setSnackbar({ open: true, message: 'Данные обновлены', severity: 'success' });
    } catch (refreshError) {
      console.error('Failed to refresh operators data:', refreshError);
      setSnackbar({ open: true, message: 'Не удалось обновить данные', severity: 'error' });
    }
  };

  const operators = useMemo(() => operatorsData?.data?.operators || [], [operatorsData]);

  useEffect(() => {
    const pendingOperators = operators.filter((operator: any) => {
      if (!operator?.id) {
        return false;
      }
      return !patternAssignmentLoadedRef.current.has(operator.id);
    });

    if (pendingOperators.length === 0) {
      return;
    }

    let isActive = true;

    (async () => {
      const assignments = await Promise.all(
        pendingOperators.map(async (operator: any) => {
          try {
            const assignment = await fetchQueuePatternAssignment({ operatorId: operator.id });
            return [operator.id, assignment] as const;
          } catch (assignmentError) {
            return [operator.id, null] as const;
          }
        }),
      );

      if (!isActive) {
        return;
      }

      setPatternAssignments((prev) => {
        const next = { ...prev };
        assignments.forEach(([operatorId, assignment]) => {
          next[operatorId] = assignment;
          patternAssignmentLoadedRef.current.add(operatorId);
        });
        return next;
      });
    })();

    return () => {
      isActive = false;
    };
  }, [operators]);

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>Управление операторами</Typography>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Ошибка загрузки операторов</Alert>
      </Box>
    );
  }
  
  // Фильтрация операторов
  const filteredOperators = operators.filter((operator: any) => {
    const matchesStatus = filterStatus === 'all' || (operator.status || 'offline') === filterStatus;
    const matchesSearch = !searchQuery || 
      `${operator.first_name || ''} ${operator.last_name || ''}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (operator.username || '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Статистика
  const totalOperators = operators.length;
  const activeOperators = operators.filter((op: any) => (op.status || 'offline') !== 'offline').length;
  const busyOperators = operators.filter((op: any) => op.status === 'busy').length;
  const totalTicketsServedToday = operators.reduce((sum: number, op: any) => {
    const value = Number(op.total_tickets_served ?? 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  const avgTickets = operators.length > 0 ? totalTicketsServedToday / operators.length : 0;

  return (
    <>
      <Helmet>
        <title>Управление операторами - ELEKTROM</title>
      </Helmet>
      
      <Box>
        {/* Заголовок */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Управление операторами
        </Typography>
            <Typography variant="body1" color="text.secondary">
              Мониторинг работы операторов и управление сессиями
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Обновить данные">
              <IconButton onClick={handleManualRefresh} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={() => handleOpenDialog('session')}
            >
              Начать сессию
            </Button>
          </Box>
        </Box>

        {/* Статистические карточки */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Всего операторов</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{totalOperators}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>в системе</Typography>
                  </Box>
                  <PeopleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Активных</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{activeOperators}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>операторов</Typography>
                  </Box>
                  <CheckCircleIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Занято</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{busyOperators}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>обслуживают</Typography>
                  </Box>
                  <AssignmentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>Среднее</Typography>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>{Math.round(avgTickets)}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>талонов/день</Typography>
                  </Box>
                  <AccessTimeIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Основная таблица операторов */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
              <TextField
                placeholder="Поиск по имени или логину..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{ flexGrow: 1 }}
              />
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Статус"
                >
                  <MenuItem value="all">Все</MenuItem>
                  <MenuItem value="available">Доступен</MenuItem>
                  <MenuItem value="busy">Занят</MenuItem>
                  <MenuItem value="break">Перерыв</MenuItem>
                  <MenuItem value="offline">Офлайн</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Оператор</TableCell>
                    <TableCell align="center">Окно</TableCell>
                    <TableCell align="center">Статус</TableCell>
                    <TableCell align="center">Обслужено сегодня</TableCell>
                    <TableCell align="center">Текущий талон</TableCell>
                    <TableCell align="center">Шаблон обслуживания</TableCell>
                    <TableCell align="center">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOperators.map((operator: any) => {
                    const assignment = patternAssignments[operator.id];
                    const assignmentPattern = assignment
                      ? queuePatterns.find((pattern) => pattern.id === assignment.patternId)
                      : undefined;
                    const assignmentLabel = assignmentPattern
                      ? assignmentPattern.name
                      : assignment
                      ? assignment.patternId
                      : 'По умолчанию';

                    return (
                      <TableRow key={operator.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar>
                            {(operator.first_name || 'N')[0]}{(operator.last_name || 'A')[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {operator.first_name || 'Имя'} {operator.last_name || 'Фамилия'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              @{operator.username || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <ComputerIcon color="action" />
                          <Typography variant="h6">
                            {operator.window_number ? `#${operator.window_number}` : '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={getStatusText(operator.status || 'offline')}
                          color={getStatusColor(operator.status || 'offline') as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="h6" color="success.main">
                          {operator.total_tickets_served || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {operator.current_ticket_full_number ? (
                          <Box>
                            <Chip
                              label={operator.current_ticket_full_number}
                              color="primary"
                              size="small"
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="caption" display="block" color="text.secondary">
                              {operator.current_queue_name}
                            </Typography>
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell align="center">
                        {assignment ? (
                          <Stack spacing={0.5} alignItems="center">
                            <Chip
                              label={assignmentLabel}
                              color={assignmentPattern?.isActive ? 'primary' : 'default'}
                              size="small"
                            />
                            <Typography variant="caption" color="text.secondary">
                              Позиция: {assignment.positionPointer + 1}
                            </Typography>
                          </Stack>
                        ) : (
                          <Chip label="По умолчанию" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Назначить шаблон">
                            <IconButton size="small" onClick={() => handleOpenPatternDialog(operator)}>
                              <AssignmentIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Просмотр">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog('view', operator)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {(operator.status === 'offline' || !operator.status) ? (
                            <Tooltip title="Начать сессию">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleOpenDialog('session', operator)}
                              >
                                <LoginIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <>
                              {operator.status === 'available' && (
                                <Tooltip title="Перерыв">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => handleStatusChange(operator.id, 'break')}
                                  >
                                    <CoffeeIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {operator.status === 'break' && (
                                <Tooltip title="Вернуться к работе">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleStatusChange(operator.id, 'available')}
                                  >
                                    <PlayIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Завершить сессию">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => endSessionMutation.mutate({ operatorId: operator.id })}
                                >
                                  <LogoutIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Диалог просмотра/управления */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {dialogType === 'session' && 'Начало рабочей сессии'}
            {dialogType === 'view' && 'Информация об операторе'}
          </DialogTitle>
          
          <DialogContent dividers>
            {dialogType === 'view' && selectedOperator && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ width: 60, height: 60 }}>
                      {(selectedOperator.first_name || 'N')[0]}{(selectedOperator.last_name || 'A')[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {selectedOperator.first_name} {selectedOperator.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedOperator.username} • {selectedOperator.role}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Статус:</Typography>
                  <Chip
                    label={getStatusText(selectedOperator.status || 'offline')}
                    color={getStatusColor(selectedOperator.status || 'offline') as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Окно:</Typography>
                  <Typography>{selectedOperator.windowNumber ? `#${selectedOperator.windowNumber}` : 'Не назначено'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Обслужено сегодня:</Typography>
                  <Typography variant="h6" color="success.main">{selectedOperator.ticketsToday || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Email:</Typography>
                  <Typography>{selectedOperator.email || 'Не указан'}</Typography>
                </Grid>
              </Grid>
            )}

            {dialogType === 'session' && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    {(selectedOperator?.first_name || 'Имя')} {(selectedOperator?.last_name || 'Фамилия')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOperator?.username}
          </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Оператор</InputLabel>
                    <Select
                      label="Оператор"
                      value={sessionOperatorId}
                      onChange={(e) => {
                        const val = String(e.target.value);
                        setSessionOperatorId(val);
                        const found = (operators || []).find((op: any) => op.id === val) || null;
                        setSelectedOperator(found);
                      }}
                    >
                      {(operators || []).map((op: any) => (
                        <MenuItem key={op.id} value={op.id}>
                          {(op.first_name || 'Имя')} {(op.last_name || 'Фамилия')} • {op.username}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Рабочее место (касса)</InputLabel>
                    <Select
                      label="Рабочее место (касса)"
                      value={selectedOperator?.workstationId || ''}
                      onChange={(e) => setSelectedOperator({ ...selectedOperator, workstationId: e.target.value })}
                    >
                      {(workstationsData?.data?.workstations || []).map((w: any) => (
                        <MenuItem key={w.id} value={w.id}>Окно #{w.window_number}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={<LoginIcon />}
                    onClick={() => {
                      const operatorId = sessionOperatorId || selectedOperator?.id;
                      if (!operatorId) { setSnackbar({ open: true, message: 'Выберите оператора', severity: 'error' }); return; }
                      if (!selectedOperator?.workstationId) { setSnackbar({ open: true, message: 'Выберите кассу', severity: 'error' }); return; }
                      startSessionMutation.mutate({ operatorId, workstationId: selectedOperator.workstationId });
                    }}
                    disabled={startSessionMutation.isLoading}
                  >
                    {startSessionMutation.isLoading ? 'Запуск...' : 'Запустить сессию'}
                  </Button>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              Закрыть
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Уведомления */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <QueuePatternAssignmentDialog
          open={patternDialogState.open}
          entityId={patternDialogState.operator?.id ?? null}
          entityName={
            patternDialogState.operator
              ? `${patternDialogState.operator.first_name || ''} ${patternDialogState.operator.last_name || ''}`.trim()
              : undefined
          }
          entityType="operator"
          patterns={queuePatterns}
          onClose={() => setPatternDialogState({ open: false, operator: null })}
          onAssigned={(assignment) => {
            const operatorId = patternDialogState.operator?.id;
            if (!operatorId) {
              return;
            }
            setPatternAssignments((prev) => ({
              ...prev,
              [operatorId]: assignment,
            }));
            patternAssignmentLoadedRef.current.add(operatorId);
          }}
        />
      </Box>
    </>
  );
};
